/**
 * Las capas del mapa, compartidas por el mapa de la red y el de señalar una ubicación.
 *
 * Con mapa de calles no se puede ubicar nada en la zona rural de Caloto ni de Quilichao:
 * ahí no hay calles, así que la pantalla queda casi en blanco y no hay contra qué
 * reconocer un cerro, un techo o el mástil. Con satélite sí.
 *
 * Esri World Imagery no pide cuenta ni clave y permite este uso. Las de Google no: usar
 * sus teselas fuera de su API va contra sus condiciones, y su API cobra.
 */

export const CAPAS = {
  satelite: {
    nombre: '🛰️ Satélite',
    url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
    atribucion: 'Imagen © Esri',
    maxZoom: 19,
  },
  calles: {
    nombre: '🗺️ Calles',
    url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
    atribucion: '© OpenStreetMap',
    maxZoom: 19,
  },
};

// Encima del satélite, los nombres de veredas y caminos. Sin esto la foto es bonita pero
// cuesta situarse; con los nombres se reconoce el sector de un vistazo.
export const ETIQUETAS = {
  url: 'https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}',
  atribucion: '',
  maxZoom: 19,
};

/**
 * Pone la capa elegida en el mapa y quita la anterior.
 *
 * @param {object} ref  Un objeto donde guardar las capas puestas, para poder quitarlas.
 * @returns la capa base añadida
 */
export const ponerCapa = (L, mapa, cual, ref) => {
  if (ref.base) mapa.removeLayer(ref.base);
  if (ref.etiquetas) { mapa.removeLayer(ref.etiquetas); ref.etiquetas = null; }

  const c = CAPAS[cual] || CAPAS.satelite;
  ref.base = L.tileLayer(c.url, { attribution: c.atribucion, maxZoom: c.maxZoom });
  ref.base.addTo(mapa);

  if (cual === 'satelite') {
    ref.etiquetas = L.tileLayer(ETIQUETAS.url, { maxZoom: ETIQUETAS.maxZoom });
    ref.etiquetas.addTo(mapa);
  }
  return ref.base;
};

/**
 * Sacar unas coordenadas de lo que sea que peguen.
 *
 * La idea es que se pueda buscar el sitio en Google Maps —que tiene mejor imagen y hasta
 * vista de calle— y traer el punto de ahí. Google entrega el enlace de varias formas
 * según cómo se copie, así que se aceptan todas en vez de exigir un formato:
 *
 *   3.0106, -76.4850                     copiado del panel de Google Maps
 *   https://maps.google.com/?q=3.01,-76.48
 *   https://www.google.com/maps/@3.0106,-76.4850,17z
 *   https://www.google.com/maps/place/.../@3.0106,-76.4850,18z/...
 *   3°00'38.2"N 76°29'06.0"W             en grados, minutos y segundos
 *
 * Devuelve null si no encuentra nada válido: es mejor no hacer nada que colocar un punto
 * en un sitio equivocado.
 */
export const leerCoordenadas = (texto) => {
  const t = (texto || '').trim();
  if (!t) return null;

  // Grados, minutos y segundos: 3°00'38.2"N 76°29'06.0"W
  const gms = t.match(
    /(\d+)[°º]\s*(\d+)['′]\s*([\d.]+)["″]?\s*([NS])[,\s]+(\d+)[°º]\s*(\d+)['′]\s*([\d.]+)["″]?\s*([EWO])/i);
  if (gms) {
    const aDecimal = (g, m, s, signo) =>
      (Number(g) + Number(m) / 60 + Number(s) / 3600) * (/[SWO]/i.test(signo) ? -1 : 1);
    const lat = aDecimal(gms[1], gms[2], gms[3], gms[4]);
    const lon = aDecimal(gms[5], gms[6], gms[7], gms[8]);
    return valido(lat, lon);
  }

  // En un enlace de Google el punto puede venir tras @ o tras q=/ll=. El de @ es el
  // centro de la pantalla y el de q= el sitio marcado: se prefiere el marcado.
  const q = t.match(/[?&](?:q|ll|daddr)=(-?\d+\.\d+)[,%2C\s]+(-?\d+\.\d+)/i);
  if (q) return valido(Number(q[1]), Number(q[2]));

  const arroba = t.match(/@(-?\d+\.\d+),(-?\d+\.\d+)/);
  if (arroba) return valido(Number(arroba[1]), Number(arroba[2]));

  // Dos números sueltos separados por coma o espacio
  const par = t.match(/^(-?\d+(?:\.\d+)?)\s*[,;\s]\s*(-?\d+(?:\.\d+)?)$/);
  if (par) return valido(Number(par[1]), Number(par[2]));

  return null;
};

/**
 * Que el punto sea posible, y que además caiga donde LinkUs trabaja.
 *
 * Lo segundo importa: al copiar un enlace es fácil traer el punto equivocado, y una torre
 * que aparezca en otro departamento no se nota hasta que alguien la busca. Se acepta una
 * zona amplia alrededor del Cauca, no un cuadrado apretado.
 */
const valido = (lat, lon) => {
  if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null;
  if (Math.abs(lat) > 90 || Math.abs(lon) > 180) return null;
  const enLaZona = lat > 0.5 && lat < 5.5 && lon > -78.5 && lon < -74.5;
  return { lat, lon, lejos: !enLaZona };
};
